/**
 * Which pending-changes keys the save endpoints can actually address.
 *
 * `pendingChanges` is keyed by whatever the editor has SELECTED, and one
 * selection has no file behind it: a chapter is a slice of project.json, so
 * `selectChapter` uses the synthetic key `chapter:<slug>`. The batch endpoint
 * only understands player-style paths (`/projects/<slug>/…`).
 *
 * Letting a synthetic key into a save batch is what turned one unsaveable edit
 * into a whole-batch failure (DEV-657): the batch URL's project slug was read
 * off the FIRST pending path, so a `chapter:` key sitting at the head of the
 * insertion-ordered map rejected every other — perfectly valid — file with it.
 * Partitioning instead of trusting `paths[0]` keeps the blast radius at the one
 * entry that is genuinely unaddressable.
 */

const PROJECT_FILE_PATH = /^\/projects\/([^/]+)\//;

/** The project slug a player-style file path belongs to, or `null` if it is not one. */
export function projectSlugFromFilePath(filePath: string): string | null {
	return PROJECT_FILE_PATH.exec(filePath)?.[1] ?? null;
}

/** Whether a path addresses a real file rather than a synthetic selection key. */
export function isProjectFilePath(filePath: string): boolean {
	return projectSlugFromFilePath(filePath) !== null;
}

export interface PartitionedPaths {
	/** The project the batch is addressed to — the first savable path's slug, or `null` when there is none. */
	slug: string | null;
	/** Paths the batch endpoint can be asked to write. */
	savable: string[];
	/** Paths with no project slug — reported as failures, never sent. */
	invalid: string[];
}

/**
 * Split pending paths into the ones a batch can carry and the ones it cannot.
 *
 * A path belonging to a *different* project is deliberately left in `savable`:
 * the backend already rejects it per file ("File does not belong to this
 * project"), which is a better message than anything this side could invent.
 */
export function partitionSavablePaths(paths: readonly string[]): PartitionedPaths {
	const savable: string[] = [];
	const invalid: string[] = [];
	let slug: string | null = null;
	for (const path of paths) {
		const pathSlug = projectSlugFromFilePath(path);
		if (pathSlug === null) { invalid.push(path); continue; }
		slug ??= pathSlug;
		savable.push(path);
	}
	return { slug, savable, invalid };
}
